/* eslint-disable space-before-function-paren */
import qs from 'qs'
import axios from 'axios'
import Toast from '@roo/roo/Toast'
import { i18nClient } from '@sailor/i18n-web'
import { getI18nParams, toSSOLogin, safeParse } from '../utils'
import { LOGIN_ERROR_CODES } from '../constants/apiCode'
import { logApi, logApiError } from '../utils/logan'
import { owlAddError } from '../utils/owl'


const failToast = (message, duration = 3500, extraProps = {}) =>
  Toast.open({
    duration,
    theme: 'light',
    position: 'center',
    status: 'fail',
    children: message,
    ...extraProps,
  })

// interceptors.request.use
// 请求拦截器：国际化请求头，i18n必须接入
export function addI18nHeader (config) {
  const i18nParams = getI18nParams()

  // 如果 region 缺失，上报 OWL 告警
  if (!i18nParams.region) {
    const apiUrl = config.url || 'unknown'
    const method = (config.method || 'unknown').toUpperCase()
    const partnerUserInfo = safeParse(sessionStorage.getItem('PARTNER_USER_INFO'), {})

    const detailInfo = {
      type: 'MissingRegionBeforeRequest',
      api: apiUrl,
      method,
      userId: partnerUserInfo.id,
      userLogin: partnerUserInfo.login,
      windowInitRegion: window?.__initRegion,
      sessionRegion: sessionStorage.getItem('region'),
      localRegion: localStorage.getItem('region'),
    }

    // OWL 告警上报（仅生产环境）
    window.__isProd && owlAddError(
      { name: 'RegionCheck: Missing Region Before Request', msg: detailInfo },
      { category: 'ajaxError', level: 'warn' }
    )
  }

  return { ...config, headers: { ...config.headers, ...i18nParams } }
}

// 请求拦截器：formdata处理
export function injectFormDataFormat (config) {
  const { data } = config
  return { ...config, data: qs.stringify(data) }
}

// interceptors.response.use
// 响应拦截器：登录信息校验统一拦截器，需要登录后才可请求的接口必须接入
export function loginCheck (response) {
  const { status, data, config } = response
  const requestHeaders = config?.headers
  const { code, msg, message } = data
  try {
    // const isLoc = isLocal(window.location.host)
    // if (isLoc) return response
    // 直接跳登录
    if (LOGIN_ERROR_CODES.indexOf(+code) !== -1) {
      console.log('LOGIN_ERROR_CODES', +code)
      failToast(i18nClient.getText('d_partner_login_text') || 'Please Login', 3500, { maxCount: 1, position: 'topCenter' })
      toSSOLogin()
    }
    return response
  } catch (e) {
    console.log(e)
    return response
  }
}

// 响应拦截器：接口错误吗处理拦截器，0成功，其他为失败，可选接入
export function codeCheck(response) {
  const { data, config } = response
  const { code, msg, message, data: realData } = data
  const {
    autoToast = true, // 是否显示Toast
    url = '',
  } = config || {}
  // 非0码弹窗报错，排除登录、越权码
  if (code !== 0 && [...LOGIN_ERROR_CODES].indexOf(+code) === -1) {
    autoToast && failToast(msg || message || 'api error', 3500, { maxCount: 1, position: 'topCenter' })
    // throw new Error(`${errorName}, ${msg || message || 'api code not 0 error'}, API: ${(url || '').replace(/\?.*$/, '')}`)
  }
  return realData
}

// 响应拦截器：返回数据处理拦截器，可选接入
export function returnAllData(response) {
  const { data } = response
  return data
}

// 接口数据全量上报Logan, 可选接入
export const reportApiLogan = response => {
  try {
    logApi(response)
  } catch (error) {
    // do nothing
    if (typeof console.warn === 'function') {
      console.warn('reportApiLogan: ', error)
    }
  }

  return response
}

export const reportApiErrorLogan = err => {
  try {
    logApiError(err)
  } catch (error) {
    if (typeof console.warn === 'function') {
      console.warn('reportApiErrorLogan: ', error)
    }
  }

  return Promise.reject(err)
}

/**
 * 接口白名单：允许region不匹配的接口列表
 * @description 这些接口由于业务特殊性，允许PARTNER_USER_INFO中的region与请求header中的region不匹配
 */
const REGION_MISMATCH_WHITELIST = [
  '/api/partner/padmin/org/r/my-info',
  '/api/partner/padmin/org/r/query-user-role',
  '/api/partner/padmin/org/r/myPartnerInfo',
  '/api/partner/padmin/org/r/query-user-api',
  '/api/partner/padmin/org/r/query-user-region',
  '/api/aigc'
]

/**
 * 检查API是否在white list中
 * @param {string} apiUrl - 完整的API URL
 * @returns {boolean}
 */
const isApiInWhitelist = (apiUrl = '') => {
  const urlPath = apiUrl.split('?')[0].split('#')[0]
  return REGION_MISMATCH_WHITELIST.some(whitelistItem => urlPath.includes(whitelistItem))
}

/**
 * 构建region检查的详细信息对象
 * @param {string} type - 异常类型
 * @param {string} apiUrl - API URL
 * @param {string} method - HTTP方法
 * @param {object} partnerUserInfo - 用户信息
 * @param {string} regionFromPartnerUserInfo - 用户的region
 * @param {string} regionFromRequestHeader - 请求header中的region
 * @param {object} params - 请求参数
 * @returns {object}
 */
const buildRegionCheckDetail = (type, apiUrl, method, partnerUserInfo, regionFromPartnerUserInfo, regionFromRequestHeader, params) => {
  const detailInfo = {
    type,
    api: apiUrl,
    method,
    userId: partnerUserInfo.id,
    userName: partnerUserInfo.name,
    userLogin: partnerUserInfo.login,
    timestamp: new Date().toISOString(),
  }

  if (regionFromPartnerUserInfo) {
    detailInfo.partnerRegion = regionFromPartnerUserInfo
  }
  if (regionFromRequestHeader) {
    detailInfo.headerRegion = regionFromRequestHeader
  }
  if (params) {
    detailInfo.params = params
  }

  return detailInfo
}

/**
 * 响应拦截器：检查并上报region不一致异常
 * @description 检测PARTNER_USER_INFO中的region和请求header中的region两者是否一致，不一致则上报owl错误
 * @note 白名单中的接口允许region不匹配
 */
export const checkRegionMismatch = response => {
  try {
    const { config } = response
    const partnerUserInfo = safeParse(sessionStorage.getItem('PARTNER_USER_INFO'), {})
    const regionFromPartnerUserInfo = partnerUserInfo?.region
    const regionFromRequestHeader = config.headers?.region
    const apiUrl = config.url || 'unknown'
    const method = config.method?.toUpperCase()

    // 如果API在白名单中，则跳过检查
    if (isApiInWhitelist(apiUrl)) {
      return response
    }

    // 如果region信息缺失或不一致，则上报
    if (!regionFromRequestHeader || !regionFromPartnerUserInfo || regionFromPartnerUserInfo !== regionFromRequestHeader) {
      let errorType = 'RegionMissing'
      let errorName = 'RegionCheck: Issue'

      if (!regionFromRequestHeader && regionFromPartnerUserInfo) {
        errorType = 'MissingHeaderRegion'
        errorName = 'RegionCheck: Missing Header Region'
      } else if (!regionFromPartnerUserInfo && regionFromRequestHeader) {
        errorType = 'MissingPartnerRegion'
        errorName = 'RegionCheck: Missing Partner Region'
      } else if (!regionFromRequestHeader && !regionFromPartnerUserInfo) {
        errorType = 'MissingBothRegion'
        errorName = 'RegionCheck: Missing Both Regions'
      } else if (regionFromPartnerUserInfo !== regionFromRequestHeader) {
        errorType = 'RegionMismatch'
        errorName = 'RegionCheck: Mismatch'
      }

      const detailInfo = buildRegionCheckDetail(
        errorType,
        apiUrl,
        method,
        partnerUserInfo,
        regionFromPartnerUserInfo,
        regionFromRequestHeader,
        config.params
      )

      window.__isProd && owlAddError({name: errorName, msg: detailInfo}, { category: 'ajaxError', level: 'warn' })
    }
  } catch (error) {
    // 静默失败，不影响正常请求
    if (typeof console.warn === 'function') {
      console.warn('checkRegionMismatch error:', error)
    }
  }

  return response
}

/**
 * 响应拦截器：检测并记录接口错误时返回中文message的情况
 * @description 当接口返回非0码且message为中文时，上报异常日志便于追踪本地化问题
 */
export const checkChineseErrorMessage = response => {
  try {
    const { data, config } = response
    const { code, msg, message } = data

    // 当code不为0时，检查msg或message是否为中文
    if (code !== 0 && /[\u4e00-\u9fa5]/.test(msg || message || '')) {
      const errorMessage = msg || message
      const partnerUserInfo = safeParse(sessionStorage.getItem('PARTNER_USER_INFO'), {})
      const detailInfo = {
        type: 'ChineseErrorMessage',
        api: config.url || 'unknown',
        code: code,
        message: errorMessage,
        userInfo: {
          id: partnerUserInfo.id,
          name: partnerUserInfo.name,
          login: partnerUserInfo.login,
        },
        timestamp: new Date().toISOString(),
      }
      const title = 'Chinese Error Message' + errorMessage.slice(0,30)
      window.__isProd && owlAddError({name: title, msg: detailInfo}, { category: 'ajaxError', level: 'warn' })
    }
  } catch (error) {
    // 静默失败，不影响正常请求
    if (typeof console.warn === 'function') {
      console.warn('checkChineseErrorMessage error:', error)
    }
  }

  return response
}
